import { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, X, Loader2, Save, ExternalLink } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { ProviderLayout } from './ProviderLayout';

const FEATURES = ['webspace','domains','vserver','dedi','discordbots','musicbots','licenses','teamspeak','gameserver'];
const FEATURE_LABELS = {
  webspace:'Webspace', domains:'Domains', vserver:'VServer', dedi:'Dedicated Server',
  discordbots:'Discord Bots', musicbots:'Musicbots', licenses:'Lizenzen',
  teamspeak:'TeamSpeak', gameserver:'Gameserver',
};

const CURRENT_YEAR = new Date().getFullYear();

export function ProviderDashboard() {
  const { authHeaders, API } = useAdminAuth();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/provider/me`, { headers: authHeaders() })
      .then(r => setForm(r.data))
      .catch(() => setError('Profil konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [API, authHeaders]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setSuccess(false); setError('');
    try {
      await axios.put(`${API}/provider/me`, form, { headers: authHeaders() });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  return (
    <ProviderLayout>
      <div className="p-4 sm:p-6 md:p-8 w-full">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Mein Anbieter-Profil</h1>
          <p className="text-sm text-slate-500 mt-1">Halte deine Informationen in der Vergleichstabelle aktuell</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
          </div>
        ) : error && !form ? (
          <div className="px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-400">{error}</div>
        ) : form ? (
          <form onSubmit={handleSave} data-testid="provider-profile-form">
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">

              {/* Left: Basic info + Profile */}
              <div className="xl:col-span-3 space-y-5">
                <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6 space-y-5">
                  <h2 className="text-sm font-semibold text-slate-300 pb-2 border-b border-slate-800">Allgemein</h2>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block">Anbieter-Name *</label>
                    <input value={form.name || ''} onChange={e => setField('name', e.target.value)}
                      required data-testid="provider-name"
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors" />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block">Website URL</label>
                    <div className="flex gap-2">
                      <input value={form.url || ''} onChange={e => setField('url', e.target.value)}
                        data-testid="provider-url" placeholder="https://deine-domain.de"
                        className="flex-1 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors" />
                      {form.url && (
                        <a href={form.url} target="_blank" rel="noopener noreferrer"
                          className="px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-cyan-400 transition-colors">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block">Tagline / Kurzbeschreibung</label>
                    <input value={form.tagline || ''} onChange={e => setField('tagline', e.target.value)}
                      data-testid="provider-tagline" placeholder="z.B. Günstiges Webhosting seit 2010"
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors" />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block">Ausführliche Beschreibung</label>
                    <textarea value={form.description || ''} onChange={e => setField('description', e.target.value)}
                      rows={4} data-testid="provider-description"
                      placeholder="Beschreibe deinen Hosting-Service, deine Besonderheiten und dein Angebot..."
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors resize-none" />
                  </div>
                </div>

                {/* Logo & Kontakt */}
                <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6 space-y-5">
                  <h2 className="text-sm font-semibold text-slate-300 pb-2 border-b border-slate-800">Logo & Kontakt</h2>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block">Logo URL</label>
                    <input value={form.logo_url || ''} onChange={e => setField('logo_url', e.target.value)}
                      data-testid="provider-logo-url" placeholder="https://deine-domain.de/logo.png"
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors" />
                    {form.logo_url && (
                      <div className="flex items-center gap-3 mt-2 p-2.5 bg-slate-800/50 border border-slate-700 rounded-lg">
                        <img src={form.logo_url} alt="Logo-Vorschau"
                          className="w-10 h-10 object-contain rounded bg-white/5"
                          onError={e => { e.target.style.display='none'; }} />
                        <span className="text-xs text-slate-500">Logo-Vorschau</span>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block">Gründungsjahr</label>
                      <input type="number" min="1990" max={CURRENT_YEAR}
                        value={form.founded_year || ''} onChange={e => setField('founded_year', e.target.value ? parseInt(e.target.value) : null)}
                        data-testid="provider-founded-year" placeholder="z.B. 2018"
                        className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block">Kontakt-E-Mail</label>
                      <input type="email" value={form.contact_email || ''} onChange={e => setField('contact_email', e.target.value)}
                        data-testid="provider-contact-email" placeholder="info@anbieter.de"
                        className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors" />
                    </div>
                  </div>
                </div>

                {success && (
                  <div data-testid="provider-save-success"
                    className="flex items-center gap-2 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-xl text-sm text-green-400">
                    <Check className="w-4 h-4 flex-shrink-0" /> Profil gespeichert
                  </div>
                )}
                {error && (
                  <div data-testid="provider-save-error"
                    className="px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-400">
                    {error}
                  </div>
                )}

                <button type="submit" disabled={saving || !form.name} data-testid="provider-save-btn"
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-xl hover:bg-cyan-400 transition-colors disabled:opacity-50 text-sm">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Änderungen speichern
                </button>
              </div>

              {/* Right: Services */}
              <div className="xl:col-span-2">
                <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6">
                  <h2 className="text-sm font-semibold text-slate-300 pb-2 border-b border-slate-800 mb-5">Angebotene Dienste</h2>
                  <div className="grid grid-cols-2 xl:grid-cols-3 gap-2.5">
                    {FEATURES.map(k => (
                      <label key={k} data-testid={`provider-feature-${k}`}
                        className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl border cursor-pointer transition-all text-sm select-none ${
                          form[k]
                            ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
                            : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600 hover:bg-slate-800'
                        }`}>
                        <input type="checkbox" checked={!!form[k]}
                          onChange={e => setField(k, e.target.checked ? 1 : 0)} className="sr-only" />
                        {form[k]
                          ? <Check className="w-4 h-4 flex-shrink-0 text-cyan-400" />
                          : <X className="w-4 h-4 flex-shrink-0 text-slate-600" />
                        }
                        <span>{FEATURE_LABELS[k]}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </form>
        ) : null}
      </div>
    </ProviderLayout>
  );
}
